import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { sendMigrationMessage } from 'api/line';
import { lineStrings } from 'constants/strings';
import { useAuth } from 'contexts/AuthContext';
import type { TSendMigrationMessageResponse } from 'types';

type ChannelResult = {
  lineChannelId: number;
  status: 'success' | 'error';
  data?: TSendMigrationMessageResponse;
  error?: string;
};

type MessageProgress = {
  current: number;
  total: number;
  results: ChannelResult[];
};

export type UseSendMigrationMessageResult = {
  send: (lineChannelIds: number[], organizationId?: number) => Promise<ChannelResult[]>;
  isRunning: boolean;
  progress: MessageProgress | null;
  reset: () => void;
};

export function useSendMigrationMessage(): UseSendMigrationMessageResult {
  const { user } = useAuth();
  const strings = lineStrings.management.migration.migrationMessage;
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState<MessageProgress | null>(null);
  const abortRef = useRef(false);
  const pendingOperationIdRef = useRef<string | null>(null);

  useEffect(() => () => { abortRef.current = true; }, []);

  const send = useCallback(
    async (lineChannelIds: number[], organizationId?: number) => {
      if (!user) return [];

      abortRef.current = false;
      setIsRunning(true);
      setProgress({ current: 0, total: lineChannelIds.length, results: [] });

      const results: ChannelResult[] = [];
      const clientMessageId = pendingOperationIdRef.current ?? uuidv4();
      pendingOperationIdRef.current = clientMessageId;
      let shouldRetry = false;

      try {
        for (const lineChannelId of lineChannelIds) {
          if (abortRef.current) break;

          try {
            const data = await sendMigrationMessage(
              lineChannelId,
              organizationId,
              clientMessageId,
            );
            if (data.failed > 0) {
              results.push({
                lineChannelId,
                status: 'error',
                data,
                error: strings.errors.sendFailed,
              });
              shouldRetry = true;
            } else {
              results.push({ lineChannelId, status: 'success', data });
            }
          } catch (caught) {
            const error = caught instanceof Error ? caught.message : strings.errors.sendFailed;
            results.push({ lineChannelId, status: 'error', error });
            shouldRetry = true;
          }

          setProgress({
            current: results.length,
            total: lineChannelIds.length,
            results: [...results],
          });
        }

        if (!shouldRetry && !abortRef.current) {
          pendingOperationIdRef.current = null;
        }
        return results;
      } finally {
        setIsRunning(false);
      }
    },
    [user, strings],
  );

  const reset = useCallback(() => {
    // 送信IDはここで消さない。曖昧な失敗の後にmodalを閉じて再試行すると
    // 別のretry keyになり、同じ移行メッセージが重複配信される。
    // 再送不要と確定した時点（送信完了時）にだけクリアする。
    abortRef.current = true;
    setIsRunning(false);
    setProgress(null);
  }, []);

  return { send, isRunning, progress, reset };
}
